import React, { useEffect, useMemo, useRef, useState } from "react";
import { setlistsApi } from "../services/setlists";
import type { PreviewResponse } from "../types/setlists";
import NumberPad from "./NumberPad";
import { ShopPlaylist } from "../types/User";
import { formatAmountForCopy, currencySymbol, isSuffixCurrency } from "../utils/currency";
import { setlistChipsFor, validateSetlistPrice } from "../utils/payinConfig";
import "./ShopPlaylists.css";
import SetlistTrackReview from "./SetlistTrackReview";
import { useSetlistReview, reviewedTracks, type ReviewState } from "../hooks/useSetlistReview";
import { copyTextToClipboard } from "../lib/clipboard";
import { formatBytes } from "../utils/formatBytes";
import { publicUrl } from "../utils/publicUrl";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// Storefront v2 — "Playlists for sale:".
//
// Up to three SAVED playlists per DJ, uploaded ahead of any sale and sold on
// repeat. Founder picks: 2C (stacked rows, so long names survive) and 3A
// (upload → name + price on ONE screen, one Save). Also:
//   · the pinned "Tonight's Setlist · For Sale" preview row while the sell
//     toggle is ON — the fan row's exact shape, so the DJ sees the shelf the
//     way fans do (tap = the existing "See what fans get" page);
//   · draft persistence — backing out of the composer keeps the upload as a
//     grey Pending row (localStorage: survives closing the app);
//   · the coherent uploader (wording + copy-link + helper, matching the
//     Balance popup) and the one-box Name+Price card.
//
// The whole component only ever mounts when the server said storefrontV2 is
// on — ProfilePage gates it. With the flag off, none of this exists.

const ACCEPTED_EXTENSIONS = [".csv", ".txt", ".xml", ".m3u", ".m3u8"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
const MAX_BYTES = 8 * 1024 * 1024; // matches the server's multer limit
const MAX_SLOTS = 3;

// The parked upload (founder decision): everything the composer needs to
// resume — the matched tracks, never the raw file (saving only posts the
// matches, so the draft is complete without it).
const DRAFT_KEY = "requestapp:shopDraft";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type ShopDraft = {
  fileName: string;
  parsed: PreviewResponse;
  name: string;
  price: number | null;
  /** The review as the talent left it (removed / swapped / added songs). Older drafts have none. */
  review?: ReviewState;
};

const readDraft = (): ShopDraft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !Array.isArray(d.parsed?.tracks)) return null;
    return d as ShopDraft;
  } catch {
    return null;
  }
};

const writeDraft = (d: ShopDraft | null) => {
  try {
    if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Storage full/blocked — the in-memory session still works; the draft
    // just won't survive an app restart.
  }
};

/** Pre-fill the name from the export's OWN date, so the common case is
 *  "accept the suggestion and tap Save". Falls back to today when the file
 *  carries no readable session date. */
function suggestName(session: PreviewResponse["session"]): string {
  const src = String(session?.startedAt || session?.dateLabel || "");
  const m = src.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  let month: string | null = null;
  let day = 0;
  if (m) {
    month = MONTH_NAMES[Number(m[1]) - 1] || null;
    day = Number(m[2]);
  }
  if (!month || day < 1 || day > 31) {
    const now = new Date();
    month = MONTH_NAMES[now.getMonth()];
    day = now.getDate();
  }
  return `${month} ${day} Set`;
}

/** Free is a real price. Everything that renders money here goes through this
 *  so a 0 can never fall through a falsiness check and read as "no price". */
export function priceLabel(price: number | null | undefined, currency?: string | null): string {
  if (price === null || price === undefined) return "—";
  if (price === 0) return tr("shopPlaylists.free");
  return formatAmountForCopy(price, currency);
}

interface Props {
  currency?: string | null;
  /** The DJ's request price — anchors the three suggested price chips. */
  requestPrice?: number | null;
  /** Sell-tonight's-set toggle state — ON pins the fan-shaped preview row. */
  liveOn?: boolean;
  /** The saved setlist price (null = none saved yet). */
  livePrice?: number | null;
  /** Opens the existing "See what fans get" preview. */
  onOpenLivePreview?: () => void;
  /** Deep link (?sheet=setlists&add=1): open the composer on mount. */
  autoOpenAdd?: boolean;
  onAutoOpenConsumed?: () => void;
  /** How many playlists are on sale now: the Profile row's On/Off follows it. */
  onShelfChange?: (count: number) => void;
}

const ShopPlaylists: React.FC<Props> = ({
  currency,
  requestPrice,
  liveOn,
  livePrice,
  onOpenLivePreview,
  autoOpenAdd,
  onAutoOpenConsumed,
  onShelfChange,
}) => {
  const [shelf, setShelf] = useState<ShopPlaylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // The add/edit sheet. `editing` holds the id when repricing/renaming an
  // existing row; null while adding a new one.
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filecap display name — survives a draft restore, where the File object
  // itself is gone (only its matches were kept).
  const [fileLabel, setFileLabel] = useState<string>("");
  const [parsed, setParsed] = useState<PreviewResponse | null>(null);
  const review = useSetlistReview(parsed);
  const [name, setName] = useState("");
  const [price, setPrice] = useState<number | null>(null);
  const [padOpen, setPadOpen] = useState(false);
  const [padDraft, setPadDraft] = useState("");
  const [draft, setDraft] = useState<ShopDraft | null>(() => readDraft());
  const [linkCopied, setLinkCopied] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const priceRowRef = useRef<HTMLDivElement | null>(null);

  const cur = (currency || "usd").toLowerCase();
  const chips = useMemo(() => setlistChipsFor(requestPrice, cur), [requestPrice, cur]);

  useEffect(() => {
    let alive = true;
    setlistsApi
      .shop()
      .then((data) => {
        if (!alive) return;
        setShelf(Array.isArray(data.playlists) ? data.playlists : []);
      })
      .catch(() => alive && setListError(tr("shopPlaylists.couldnTLoadYour")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // A failed read says nothing about the shelf, so it reports nothing.
  useEffect(() => {
    if (!loading && !listError) onShelfChange?.(shelf.length);
  }, [loading, listError, shelf.length, onShelfChange]);

  const resetComposer = () => {
    setFileLabel("");
    setParsed(null);
    review.reset();
    setName("");
    setPrice(null);
    setPadOpen(false);
    setPadDraft("");
    setError(null);
    setEditing(null);
  };

  // What goes on the shelf is the reviewed list (SetlistTrackReview): only
  // songs with a Spotify match — an unmatched row can't be delivered to
  // Spotify or Apple Music, so shipping it would put a song on the tracklist
  // the fan never receives.
  const usableTracks = useMemo(() => review.finalTracks.filter((t) => t.spotifyId), [review.finalTracks]);
  // How many rows the file itself had, vs how many we could match. The two
  // numbers answer different questions ("did you read my file?" and "can you
  // actually deliver it?") and collapsing them into one is how a DJ ends up
  // staring at "0 tracks read" for a file that parsed perfectly.
  const parsedCount = parsed?.tracks?.length ?? 0;
  const nothingMatched = parsedCount > 0 && !(parsed?.tracks || []).some((t) => t.match?.id);

  // Back arrow with an unsaved upload → park it (founder: "it
  // should still save it so I can come back"). The Pending row below is the
  // way back in.
  const closeComposer = () => {
    if (!editing && parsed && usableTracks.length > 0) {
      const d: ShopDraft = { fileName: fileLabel, parsed, name, price, review: review.snapshot };
      setDraft(d);
      writeDraft(d);
    }
    setComposerOpen(false);
    resetComposer();
  };

  const openAdd = () => {
    resetComposer();
    setComposerOpen(true);
  };

  // Deep link (?sheet=setlists&add=1) — straight into the upload screen.
  useEffect(() => {
    if (!autoOpenAdd) return;
    openAdd();
    onAutoOpenConsumed?.();
    // Runs once when the deep-link flag flips; openAdd / onAutoOpenConsumed
    // are recreated by the parent every render and must not re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd]);

  const openDraft = () => {
    if (!draft) return;
    resetComposer();
    setFileLabel(draft.fileName);
    setParsed(draft.parsed);
    review.reset(draft.review);
    setName(draft.name);
    setPrice(draft.price ?? null);
    setComposerOpen(true);
  };

  const clearDraft = () => {
    setDraft(null);
    writeDraft(null);
  };

  const openEdit = (p: ShopPlaylist) => {
    resetComposer();
    setEditing(p.id);
    setName(p.name);
    setPrice(p.price);
    setComposerOpen(true);
  };

  const handleFiles = async (list: FileList | File[] | null) => {
    setError(null);
    if (!list || list.length === 0) return;
    const first = list[0];
    const ext = (first.name.toLowerCase().match(/\.[a-z0-9]+$/) || [""])[0];
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setError(tr("shopPlaylists.thatFileTypeIsn", { ACCEPTED_EXTENSIONS: ACCEPTED_EXTENSIONS.join(", ") }));
      return;
    }
    if (first.size > MAX_BYTES) {
      setError(tr("shopPlaylists.fileIsTooLarge", { formatBytes: formatBytes(first.size) }));
      return;
    }
    setFileLabel(first.name);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", first);
    try {
      const data = await setlistsApi.preview(fd);
      setParsed(data);
      review.reset();
      setName((n) => n || suggestName(data.session));
      // Middle chip as the opening suggestion — a shelf playlist is a
      // considered purchase, not an impulse one.
      setPrice((p) => (p === null ? chips[1] ?? chips[0] ?? null : p));
    } catch (err: any) {
      setFileLabel("");
      setError(err?.response?.data?.msg || tr("shopPlaylists.weCouldnTRead"));
    } finally {
      setBusy(false);
    }
  };

  const commitPad = () => {
    const t = padDraft.trim();
    if (t !== "") {
      const n = Number(t.replace(",", "."));
      const check = validateSetlistPrice(n, cur);
      if (check.ok) setPrice(check.price!);
      else setError(tr("shopPlaylists.thatPriceWonT"));
    }
    setPadDraft("");
    setPadOpen(false);
  };

  // Mobile → computer hand-off (mirroring the Balance uploader):
  // copies the deep link that reopens THIS screen — profile drawer, Sell
  // Playlists sheet, composer — on the laptop where the Serato file lives.
  const onCopyLink = () => {
    const url = publicUrl("/requests?sheet=setlists&add=1#profile");
    copyTextToClipboard(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const canSave =
    !busy &&
    name.trim().length > 0 &&
    price !== null &&
    (editing !== null || usableTracks.length > 0);

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        const data = await setlistsApi.updateShopPlaylist(editing, { name: name.trim(), price });
        setShelf(shelf.map((p) => (p.id === editing ? data.playlist : p)));
      } else {
        const data = await setlistsApi.createShopPlaylist({
          name: name.trim(),
          price,
          tracks: usableTracks,
        });
        setShelf([...shelf, data.playlist]);
        // On sale — the parked copy has served its purpose.
        clearDraft();
      }
      setComposerOpen(false);
      resetComposer();
    } catch (err: any) {
      setError(err?.response?.data?.msg || tr("shopPlaylists.couldnTSaveThat"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: ShopPlaylist) => {
    setBusy(true);
    setError(null);
    try {
      await setlistsApi.deleteShopPlaylist(p.id);
      setShelf(shelf.filter((x) => x.id !== p.id));
      setComposerOpen(false);
      resetComposer();
    } catch (err: any) {
      setError(err?.response?.data?.msg || tr("shopPlaylists.couldnTRemoveThat"));
    } finally {
      setBusy(false);
    }
  };

  const priceIsChip = (c: number) => price !== null && Math.abs(price - c) < 0.005;
  const priceIsCustom = price !== null && !chips.some(priceIsChip) && price !== 0;

  // What the parked review would put on sale — the same count the composer shows.
  const draftTrackCount = draft
    ? reviewedTracks(draft.parsed.tracks, { decisions: draft.review?.decisions || {}, added: draft.review?.added || [] }).filter((t) => t.spotifyId).length
    : 0;

  return (
    <>
      <div className="shop-lbl">
        {tr("shopPlaylists.playlistsForSale")}
        {shelf.length > 0 && <span className="shop-count">{tr("shopPlaylists.of", { length: shelf.length, MAX_SLOTS })}</span>}
      </div>

      {listError && <div className="shop-err">{listError}</div>}

      <div className="shop-rows">
        {/* Tonight's set, the way fans see it (founder decision): pinned
            first while the sell toggle is ON, the fan row's exact shape —
            generic waveform tile (nothing exists yet), grey "For Sale" next
            to "What fans see", price in the sub like every other row. Never
            counts against the 3 slots; tap = "See what fans get". */}
        {liveOn && (
          <button type="button" className="shop-row shop-row--live" onClick={onOpenLivePreview}>
            <span className="shop-live-art" aria-hidden>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#24C961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h3l2-7 4 14 3-9 2 2h4" />
              </svg>
            </span>
            <span className="shop-row-mid">
              <span className="shop-row-t">{tr("shopPlaylists.tonightSSetlist")}</span>
              <span className="shop-row-m">
                <T k="shopPlaylists.whatFansSeeFor" vars={{ amount: priceLabel(livePrice, cur) }} tags={{ 0: (c) => <b>{c}</b> }} />
              </span>
            </span>
            <span className="shop-row-chev" aria-hidden>›</span>
          </button>
        )}

        {shelf.map((p) => (
          <button key={p.id} type="button" className="shop-row" onClick={() => openEdit(p)}>
            <span className="shop-row-art" aria-hidden>
              {p.covers.length > 0 ? (
                p.covers.slice(0, 4).map((src, i) => <img key={i} src={src} alt="" />)
              ) : (
                <span className="shop-row-art-blank" />
              )}
            </span>
            <span className="shop-row-mid">
              <span className="shop-row-t">{p.name}</span>
              <span className="shop-row-m">
                {tr("shopPlaylists.tracksCount", { count: p.trackCount })} ·{" "}
                <b className={p.price === 0 ? "free" : undefined}>{priceLabel(p.price, cur)}</b>
              </span>
            </span>
            <span className="shop-row-chev" aria-hidden>›</span>
          </button>
        ))}

        {/* The parked upload — back-arrowed out before "Put it on sale".
            Grey on purpose: "not for sale yet" against the preview row's
            "For Sale" (founder, build 34). */}
        {draft && !loading && shelf.length < MAX_SLOTS && (
          <button type="button" className="shop-row shop-row--draft" onClick={openDraft}>
            <span className="shop-row-art" aria-hidden>
              {draft.parsed.tracks
                .filter((t) => t.match?.image)
                .slice(0, 4)
                .map((t, i) => <img key={i} src={t.match!.image!} alt="" />)}
              {draft.parsed.tracks.filter((t) => t.match?.image).length === 0 && (
                <span className="shop-row-art-blank" />
              )}
            </span>
            <span className="shop-row-mid">
              <span className="shop-row-t">{draft.name || tr("shopPlaylists.untitledSet")}</span>
              <span className="shop-row-m">
                {tr("shopPlaylists.tracksNotForSale", { count: draftTrackCount })}
              </span>
            </span>
            <span className="shop-chip-pending">{tr("shopPlaylists.pending")}</span>
          </button>
        )}

        {!loading &&
          Array.from({
            length: Math.max(0, MAX_SLOTS - shelf.length - (draft ? 1 : 0)),
          }).map((_, i) => (
            <button key={`add-${i}`} type="button" className="shop-row shop-row--add" onClick={openAdd}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#4ade80" strokeWidth="2.6" strokeLinecap="round" aria-hidden>
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span className="shop-row-t">{tr("shopPlaylists.addAPlaylist")}</span>
            </button>
          ))}
      </div>

      {/* Founder pick 4A — the one punchy line. */}
      <div className="shop-explain">
        <T k="shopPlaylists.dropASeratoFile" tags={{ 0: (c) => <b>{c}</b> }} />
      </div>

      {/* ── Composer: upload → name → price, one screen (founder pick 3A) ── */}
      {composerOpen && (
        <div className="shop-composer" role="dialog" aria-modal="true" aria-label={editing ? tr("shopPlaylists.editPlaylist") : tr("shopPlaylists.newPlaylist")}>
          <div className="shop-composer-head">
            <button type="button" className="shop-back" onClick={closeComposer} aria-label={tr("shopPlaylists.back")}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <span className="shop-composer-title">{editing ? tr("shopPlaylists.editPlaylist") : tr("shopPlaylists.newPlaylist")}</span>
          </div>

          <div className={`shop-composer-body${padOpen ? " shop-composer-body--pad" : ""}`}>
            {/* Step 1 — the file. Skipped when editing: the tracklist is
                immutable so a past sale can never be rewritten under the fan
                who bought it. */}
            {!editing && (
              <>
                {!parsed ? (
                  <>
                    <button
                      type="button"
                      className="shop-drop"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={busy}
                    >
                      {busy ? (
                        <>
                          <span className="shop-spin" aria-hidden />
                          <span className="shop-drop-t">{tr("shopPlaylists.reading", { fileLabel })}</span>
                          <span className="shop-drop-s">{tr("shopPlaylists.matchingEveryTrackThis")}</span>
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#24C961" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <path d="M12 16V4M7 9l5-5 5 5" />
                            <path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
                          </svg>
                          <span className="shop-drop-t">{tr("shopPlaylists.uploadTrackHistory")}</span>
                          <span className="shop-drop-s">{tr("shopPlaylists.seratoRekordboxEngineDj")}</span>
                        </>
                      )}
                    </button>
                    {/* Same hand-off + helper as the Balance uploader
                        (founder: "make them coherent"). The link deep-links
                        straight back to this screen on the laptop. */}
                    {!busy && (
                      <>
                        <div className="shop-copylink" aria-live="polite">
                          <span className="shop-copylink-text">{tr("shopPlaylists.onMobileCopyThis")}</span>
                          <button type="button" className="shop-copylink-btn" onClick={onCopyLink} aria-label={tr("shopPlaylists.copyLinkToClipboard")}>
                            {linkCopied ? (
                              <span className="shop-copylink-done">{tr("shopPlaylists.copied")}</span>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                                <span>{tr("shopPlaylists.copy")}</span>
                              </>
                            )}
                          </button>
                        </div>
                        <p className="shop-helper">
                          <T k="setlistUploadFlow.exportFromSeratoHistory" tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong>, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong> }} />
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <div className={`shop-filecap${nothingMatched ? " shop-filecap--warn" : ""}`}>
                    {nothingMatched ? (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#fbbf24" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                        <path d="M12 8v5M12 17h.01" />
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#24C961" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                    <div>
                      <div className="shop-filecap-t">
                        {nothingMatched
                          ? tr("shopPlaylists.readNoneFound", { count: parsedCount })
                          : tr("shopPlaylists.tracksReady", { count: usableTracks.length })}
                      </div>
                      <div className="shop-filecap-s">
                        {nothingMatched ? tr("shopPlaylists.clubEditsAndBootlegs") : fileLabel}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shop-filecap-x"
                      onClick={() => {
                        // Replacing abandons this upload — the parked copy
                        // goes with it.
                        setFileLabel("");
                        setParsed(null);
                        review.reset();
                        clearDraft();
                      }}
                    >
                      {tr("shopPlaylists.replace")}
                    </button>
                  </div>
                )}
                {/* The same review as after a sale: ✕ removes, Confirm or
                    Swap the unsure ones, + adds (founder, 10/10). */}
                {parsed && !nothingMatched && (
                  <div className="shop-review">
                    <SetlistTrackReview review={review} />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  onChange={(e) => handleFiles(e.target.files)}
                  style={{ display: "none" }}
                />
              </>
            )}

            {/* Step 2 — name + price, ONE box (founder decision). */}
            {((parsed && !nothingMatched) || editing) && (
              <div className="shop-card">
                <div className="shop-cap">{tr("shopPlaylists.name")}</div>
                <input
                  className="shop-name"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 60))}
                  placeholder={tr("shopPlaylists.nameThisPlaylist")}
                  maxLength={60}
                />
                <div className="shop-hint">{tr("shopPlaylists.thisIsWhatFans")}</div>
                <div className="shop-card-divider" aria-hidden />
                <div className="shop-cap" ref={priceRowRef}>{tr("shopPlaylists.price")}</div>
                <div className="shop-chips">
                  {chips.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`shop-chip${priceIsChip(c) ? " on" : ""}`}
                      onClick={() => setPrice(c)}
                    >
                      {formatAmountForCopy(c, cur)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`shop-chip shop-chip--custom${priceIsCustom || price === 0 || padOpen ? " on" : ""}`}
                    onClick={() => {
                      setPadDraft("");
                      setPadOpen(true);
                      // The pad mounts fixed at the bottom — draw the price
                      // row up above it so the amount stays in view, on the
                      // smallest phones too. .shop-composer-body--pad is what
                      // gives it somewhere to go.
                      setTimeout(() => {
                        priceRowRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
                      }, 60);
                    }}
                  >
                    {padOpen && padDraft
                      ? `${!isSuffixCurrency(cur) ? currencySymbol(cur) : ""}${padDraft}${isSuffixCurrency(cur) ? ` ${currencySymbol(cur)}` : ""}`
                      : price === 0
                      ? tr("shopPlaylists.free")
                      : priceIsCustom
                      ? formatAmountForCopy(price!, cur)
                      : tr("shopPlaylists.custom")}
                  </button>
                </div>
              </div>
            )}

            {error && <div className="shop-err">{error}</div>}

            {((parsed && !nothingMatched) || editing) && (
              <button type="button" className="shop-save" onClick={save} disabled={!canSave}>
                {busy ? tr("shopPlaylists.saving") : editing ? tr("shopPlaylists.saveChanges") : tr("shopPlaylists.putItOnSale")}
              </button>
            )}

            {editing && (
              <button
                type="button"
                className="shop-remove"
                onClick={() => remove(shelf.find((p) => p.id === editing)!)}
                disabled={busy}
              >
                {tr("shopPlaylists.removeFromMyPage")}
              </button>
            )}
          </div>

          <NumberPad
            open={padOpen}
            value={padDraft}
            onChange={setPadDraft}
            onDone={commitPad}
            allowDecimal
            withBackdrop
            maxLength={6}
          />
        </div>
      )}
    </>
  );
};

export default ShopPlaylists;
